import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  useCalendarDispatchContext,
  useCalendarStateContext
} from 'providers/Calendar';
import {
  CALENDAR_EVENT_STATUSES,
  CALENDAR_EVENT_TYPES,
  CALENDAR_VIEW
} from 'providers/Calendar/constants';
import { CalendarViewType } from 'providers/Calendar/types';
import { useNavigate } from 'react-router-dom';
import { DoctorProfile, fetchProfile } from 'services/api/profile';
import FullCalendar from '@fullcalendar/react';
import {
  AllowFunc,
  DateSelectArg,
  DatesSetArg,
  EventChangeArg,
  EventClickArg
} from '@fullcalendar/core';

import { PageWrapper } from 'components/ui';
import { Content } from './styled';
import { generateUniqueId } from 'utils/id';
import { EventDropdown } from './components';
import { IEventDropdownRef } from './components/EventDropdown/types';
import { DAY_NUMBERS } from './constants';
import { WorkingHoursType } from 'pages/MyProfile/components/WorkingHours/types';
import { parseJson } from 'utils/json';

const WINDOW_SPACE = 100;

const getStartOfDay = (date: Date) => dayjs(date).startOf('day').format();

const getEndOfDay = (date: Date) =>
  dayjs(date)
    .endOf('day')
    .set('hours', 23)
    .set('minutes', 30)
    .set('seconds', 0)
    .format();

const Calendar = () => {
  // navigation
  const navigate = useNavigate();
  // translations
  const { t } = useTranslation();
  // refs
  const calendarRef = useRef<FullCalendar | null>(null);
  const eventDropdownRef = useRef<IEventDropdownRef | null>(null);
  // provider
  const { view, events, appointments } = useCalendarStateContext();
  const { setView, addEvent, updateEvent, setActiveEvent, setDates } =
    useCalendarDispatchContext();

  // states
  const [calendarHeight, setCalendarHeight] = useState(
    window.innerHeight - WINDOW_SPACE
  );

  // profile (working hours) via REST
  const [profile, setProfile] = useState<DoctorProfile | null>(null);

  useEffect(() => {
    fetchProfile()
      .then(setProfile)
      .catch(() => setProfile(null));
  }, []);

  const closeDropdown = useCallback(() => {
    eventDropdownRef.current?.close();
  }, []);

  const onSelect = useCallback(
    (e: DateSelectArg) => {
      closeDropdown();
      const id = generateUniqueId();

      if (e.jsEvent) {
        // if selected all day format it
        const start =
          view === CALENDAR_VIEW.month ? getStartOfDay(e.start) : e.startStr;

        const end =
          view === CALENDAR_VIEW.month ? getEndOfDay(e.start) : e.endStr;

        addEvent({
          event: {
            id,
            start,
            end,
            title: '',
            extendedProps: {
              status: CALENDAR_EVENT_STATUSES.new
            }
          },
          ui: {
            id,
            x: e.jsEvent.x,
            y: e.jsEvent.y
          }
        });
      }
    },
    [addEvent, closeDropdown, view]
  );

  const onUpdate = (e: EventChangeArg) => {
    closeDropdown();
    const current = events.find(item => item.id === e.event.id);

    updateEvent({
      title: '',
      ...current,
      id: e.event.id,
      start: e.event.startStr,
      end: e.event.endStr
    });
  };

  const onEventClick = (e: EventClickArg) => {
    closeDropdown();

    if (e.event.extendedProps.type === CALENDAR_EVENT_TYPES.appointment) {
      return navigate(`/appointments/details/${e.event.id}`);
    }

    setActiveEvent({
      id: e.event.id,
      x: e.jsEvent.x,
      y: e.jsEvent.y
    });
  };

  const onChangeView = (value: CalendarViewType) => {
    closeDropdown();
    calendarRef.current?.getApi().changeView(value);
    setView(value);
  };

  const onClickToday = () => {
    closeDropdown();
    calendarRef.current?.getApi().gotoDate(new Date());
  };

  const selectAllow: AllowFunc = useCallback(
    e => {
      switch (view) {
        case CALENDAR_VIEW.month:
          return Math.abs(e.start.getDay() - e.end.getDay()) <= 1;
        default:
          return e.start.getDay() === e.end.getDay();
      }
    },
    [view]
  );

  const businessHours = useMemo(() => {
    const days: WorkingHoursType = parseJson(profile?.working_hours);

    if (!days) return [];

    const hours = Object.keys(days)
      .filter(day => days[day as keyof typeof DAY_NUMBERS].enabled)
      .map(day =>
        days[day as keyof typeof DAY_NUMBERS].hours.map(item => ({
          startTime: item.start,
          endTime: item.end,
          daysOfWeek: [DAY_NUMBERS[day as keyof typeof DAY_NUMBERS]]
        }))
      )
      .flat();

    return hours;
  }, [profile?.working_hours]);

  const onDatesSet = useCallback(
    (dates: DatesSetArg) => {
      setDates(dates.start, dates.end);
    },
    [setDates]
  );

  useEffect(() => {
    const onResize = () => {
      setCalendarHeight(window.innerHeight - WINDOW_SPACE);
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <PageWrapper>
      <Content>
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
          initialView={view}
          events={[...events, ...appointments]}
          select={onSelect}
          eventChange={onUpdate}
          eventClick={onEventClick}
          allDaySlot={false}
          height={calendarHeight}
          selectAllow={selectAllow}
          eventAllow={e => e.start.getDay() === e.end.getDay()}
          headerToolbar={{
            right: 'today prev,next timeGridDay,timeGridWeek,dayGridMonth'
          }}
          businessHours={businessHours}
          datesSet={onDatesSet}
          views={{
            timeGridWeek: {
              dayHeaderFormat: {
                omitZeroMinute: true,
                weekday: 'short',
                day: 'numeric'
              }
            }
          }}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            hour12: false,
            meridiem: false
          }}
          customButtons={{
            today: {
              text: t('calendar.today'),
              click: onClickToday
            },
            timeGridDay: {
              text: t('calendar.day'),
              click: () => onChangeView(CALENDAR_VIEW.day)
            },
            timeGridWeek: {
              text: t('calendar.week'),
              click: () => onChangeView(CALENDAR_VIEW.week)
            },
            dayGridMonth: {
              text: t('calendar.month'),
              click: () => onChangeView(CALENDAR_VIEW.month)
            }
          }}
          selectable
          editable
        />
        <EventDropdown ref={eventDropdownRef} />
      </Content>
    </PageWrapper>
  );
};

export default Calendar;
